import { twMerge } from "tailwind-merge";
import { InnerContentFrame } from "../InnerContentFrame";
import { Button } from "../Button";
import { StepProgress } from "../StepProgress";

export type Props = {
  /** Current step index (0-indexed) */
  stepIndex: number;
  /** Total number of steps */
  totalSteps: number;
  /** Primary button label */
  primaryLabel: string;
  /** Handler for primary button click */
  onPrimaryClick: () => void;
  /** Whether primary button is disabled */
  isPrimaryDisabled?: boolean;
  /** Whether primary button is in loading state */
  isPrimaryLoading?: boolean;
  /** Handler for back button (hidden if stepIndex is 0 or undefined) */
  onBack?: () => void;
  /** Handler for skip button (hidden if undefined) */
  onSkip?: () => void;
  /** Skip button label */
  skipLabel?: string;
  /** Whether to show progress indicator */
  showProgress?: boolean;
  /** Prefix for progress indicator (e.g., "Step") */
  progressPrefix?: string;
  /** Additional CSS classes for the container */
  className?: string;
};

/**
 * Step action bar with progress indicator and navigation buttons.
 * Used for multi-step flows like quizzes and onboarding.
 *
 * @example
 * // Quiz-style actions (no prefix)
 * <StepActions
 *   stepIndex={0}
 *   totalSteps={5}
 *   primaryLabel="Next"
 *   onPrimaryClick={handleNext}
 * />
 *
 * @example
 * // Onboarding-style actions (with prefix and skip)
 * <StepActions
 *   stepIndex={1}
 *   totalSteps={3}
 *   primaryLabel="Continue"
 *   onPrimaryClick={handleContinue}
 *   onBack={handleBack}
 *   onSkip={handleSkip}
 *   progressPrefix="Step"
 * />
 */
export function StepActions({
  stepIndex,
  totalSteps,
  primaryLabel,
  onPrimaryClick,
  isPrimaryDisabled = false,
  isPrimaryLoading = false,
  onBack,
  onSkip,
  skipLabel = "Skip for now",
  showProgress = true,
  progressPrefix,
  className,
}: Props) {
  const showBackButton = stepIndex > 0 && onBack;

  return (
    <InnerContentFrame
      className={twMerge(
        "bg-brand-50 text-brand-800 border-t border-brand-800",
        className
      )}
      innerClassName="gap-2 px-4 py-4 md:px-8 md:py-4 md:justify-center md:items-center"
      data-testid="step-actions"
    >
      {showProgress && (
        <StepProgress
          currentStep={stepIndex + 1}
          totalSteps={totalSteps}
          prefix={progressPrefix}
        />
      )}
      <Button
        onClick={onPrimaryClick}
        type="submit"
        disabled={isPrimaryDisabled || isPrimaryLoading}
        isLoading={isPrimaryLoading}
        className="w-full"
        data-testid="step-actions-primary"
      >
        {primaryLabel}
      </Button>
      {showBackButton && (
        <Button
          onClick={onBack}
          variant="link"
          className="w-full"
          data-testid="step-actions-back"
        >
          Back
        </Button>
      )}
      {onSkip && (
        <Button
          onClick={onSkip}
          variant="link"
          className="w-full"
          data-testid="step-actions-skip"
        >
          {skipLabel}
        </Button>
      )}
    </InnerContentFrame>
  );
}

export default StepActions;
